import type { TFunction } from "i18next";

import type { Route } from "../../../store/admin/routesStore";
import type { Trip } from "../../../store/admin/tripsStore";
import type { Unit } from "../../../store/admin/unitsStore";
import type {
  ChatThreadSummary,
  ChipTone,
  Protocol,
  ProtocolActionId,
  RouteInsight,
  TripCustodyState,
  TripWatchState,
  UnitWatchState,
  WatchStatus,
} from "../types";

export interface MockUnitRef {
  id: string;
  name: string;
}

const MINUTE_MS = 60_000;

const minutesAgo = (minutes: number) => new Date(Date.now() - minutes * MINUTE_MS).toISOString();

const hashOf = (value: string) =>
  [...value].reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) % 100_000, 7);

const pick = <T,>(items: readonly T[], seed: number) => items[seed % items.length];

const STATUS_CYCLE: readonly WatchStatus[] = [
  "safe",
  "warning",
  "alert",
  "offline",
  "safe",
  "safe",
  "warning",
  "safe",
  "safe",
  "safe",
];

const INCIDENT_KEYS = ["lowTire", "authorizedRest", "trafficJam", "unscheduledStop"] as const;

export function buildWatchStatusMap(units: MockUnitRef[]): Map<string, WatchStatus> {
  return new Map(
    [...units]
      .sort((a, b) => a.id.localeCompare(b.id))
      .map((unit, index): [string, WatchStatus] => [
        unit.id,
        STATUS_CYCLE[index % STATUS_CYCLE.length],
      ]),
  );
}

export const isDemoRecord = (id: string): boolean => id.startsWith("demo-");

export const TEST_CALL_KEYWORDS = { word: "girasol", duress: "amapola" } as const;

const DEMO_UNIT_COUNT = 13;

const stamp = () => ({ _seconds: Math.round(Date.now() / 1000), _nanoseconds: 0 });

const DEMO_DRIVERS = [
  "Daniel Gómez",
  "Sofía Martínez",
  "Luis Herrera",
  "Ana Ramírez",
  "Jorge Castillo",
  "Marisol Peña",
  "Iván Domínguez",
  "Rocío Villalobos",
  "Emilio Zavala",
  "Karla Fuentes",
  "Ricardo Nájera",
  "Paulina Ocampo",
] as const;

const DEMO_PLACES = [
  "Av. Reforma 210, CDMX",
  "Periférico Sur 3050, Puebla",
  "Carretera Federal 150, Veracruz",
  "Blvd. Díaz Ordaz 1200, Monterrey",
  "Libramiento Norte 45, Querétaro",
  "Av. Vallarta 890, Guadalajara",
  "Eje 6 Sur 1450, Iztapalapa",
  "Autopista Arco Norte km 82, Tepeji",
  "Carretera Panamericana 411, Saltillo",
  "Av. López Mateos Sur 2375, Zapopan",
  "Puerto Interior km 12, Silao",
  "Carretera a Laredo km 26, Apodaca",
  "Blvd. Aeropuerto 780, Toluca",
  "Av. Tulum 320, Cancún",
] as const;

export function buildDemoUnits(): Unit[] {
  return Array.from({ length: DEMO_UNIT_COUNT }, (_, index) => {
    const number = `${index + 1}`.padStart(2, "0");
    const id = `demo-unit-${number}`;
    const seed = hashOf(id);

    return {
      id,
      name: `U-${number}`,
      plate: `DEM-${1000 + seed % 9000}`,
      type: "truck",
      createdAt: stamp(),
      updatedAt: stamp(),
      collections: [],
      devices: [],
      lastAddress: pick(DEMO_PLACES, seed),
      driver: { id: `demo-driver-${number}`, name: pick(DEMO_DRIVERS, seed) },
    };
  });
}

const DEMO_GEOFENCE_COUNT = 12;

const DEMO_ROUTE_KEYS = [
  "cdmxPuebla",
  "monterreySaltillo",
  "gdlColima",
  "queretaroLeon",
  "cdmxVeracruz",
  "tolucaMorelia",
  "pueblaOaxaca",
  "monterreyLaredo",
  "gdlAguascalientes",
  "cancunMerida",
] as const;

export function buildDemoRoutes(t: TFunction): Route[] {
  return DEMO_ROUTE_KEYS.map((key, index) => {
    const id = `demo-route-${index}`;
    const seed = hashOf(id);
    const inactive = index % 8 === 7;

    return {
      id,
      name: t(`monitoreoIa.demo.routes.${key}.name`),
      description: t(`monitoreoIa.demo.routes.${key}.description`),
      status_id: inactive ? "inactive" : "active",
      status: {
        id: inactive ? "inactive" : "active",
        name: t(`monitoreoIa.demo.statuses.${inactive ? "inactive" : "active"}`),
      },
      geofences_id: [`demo-geofence-${index % DEMO_GEOFENCE_COUNT}`],
      principal_route: [],
      principal_route_distance: 120_000 + (seed % 400) * 1_000,
      first_point_radius: 250,
      last_point_radius: 250,
      company_id: "demo",
      vehicles_count: 1 + (seed % 5),
      createdAt: stamp(),
      updatedAt: stamp(),
    };
  });
}

const DEMO_TRIP_STATUSES = [
  { key: "inProgress", color: "#22c55e" },
  { key: "scheduled", color: "#3b82f6" },
  { key: "completed", color: "#8b5cf6" },
  { key: "cancelled", color: "#ef4444" },
] as const;

const DEMO_TRIP_NAME_KEYS = [
  "nightDelivery",
  "spareParts",
  "warehouseTransfer",
  "portPickup",
  "localDistribution",
  "refrigeratedRun",
  "borderExport",
  "retailRestock",
  "bulkFuel",
  "expressCourier",
  "constructionSupply",
  "returnEmpties",
  "interplantShuttle",
  "weekendRelay",
] as const;

const DEMO_TRIP_PRIORITIES = ["high", "medium", "low"] as const;

export function buildDemoTrips(t: TFunction): Trip[] {
  return DEMO_TRIP_NAME_KEYS.map((nameKey, index) => {
    const id = `demo-trip-${index}`;
    const seed = hashOf(id);
    const status = pick(DEMO_TRIP_STATUSES, index);
    const routeIndex = index % DEMO_ROUTE_KEYS.length;
    const routeKey = DEMO_ROUTE_KEYS[routeIndex];

    return {
      id,
      title: t(`monitoreoIa.demo.trips.names.${nameKey}`),
      description: t(`monitoreoIa.demo.routes.${routeKey}.description`),
      priority: pick(DEMO_TRIP_PRIORITIES, seed),
      route_id: `demo-route-${routeIndex}`,
      status_id: status.key,
      status: {
        id: status.key,
        name: t(`monitoreoIa.demo.trips.statuses.${status.key}`),
        color: status.color,
      },
      route: {
        id: `demo-route-${routeIndex}`,
        name: t(`monitoreoIa.demo.routes.${routeKey}.name`),
        description: t(`monitoreoIa.demo.routes.${routeKey}.description`),
      },
      origin: pick(DEMO_PLACES, seed).split(",")[0],
      destiny: pick(DEMO_PLACES, seed + 5).split(",")[0],
      createdAt: stamp(),
      updatedAt: stamp(),
    };
  });
}

const STATUS_CHIP_KEYS = {
  safe: [
    { key: "onRoute", tone: "success" },
    { key: "normalSpeed", tone: "success" },
  ],
  warning: [
    { key: "unscheduledStop", tone: "warning" },
    { key: "pendingCheck", tone: "info" },
  ],
  alert: [
    { key: "routeDeviation", tone: "error" },
    { key: "verifying", tone: "warning" },
  ],
  risk: [
    { key: "routeDeviation", tone: "error" },
    { key: "verifying", tone: "warning" },
  ],
  offline: [
    { key: "noSignal", tone: "neutral" },
    { key: "lastKnown", tone: "neutral" },
  ],
  sin_custodia: [
    { key: "noOperatorPhone", tone: "info" },
    { key: "detectionOnly", tone: "info" },
  ],
} as const satisfies Record<WatchStatus, readonly { key: string; tone: ChipTone }[]>;

export function buildUnitWatchStates(t: TFunction, units: MockUnitRef[]): UnitWatchState[] {
  const statuses = buildWatchStatusMap(units);

  return units.map((unit) => {
    const seed = hashOf(unit.id);
    const status = statuses.get(unit.id) ?? "safe";
    const needsIncident = status === "warning" || status === "alert";
    const isMoving = status === "safe" || status === "alert";

    return {
      unitId: unit.id,
      deviceId: null,
      status,
      hasOperatorPhone: seed % 5 !== 0,
      underCustody: status !== "sin_custodia",
      openCaseId: needsIncident ? `demo-case-${seed % 97}` : null,
      isCalling: false,
      address: null,
      aiMessageType: needsIncident ? "warn" : "info",
      speedKph: isMoving ? 48 + (seed % 62) : 0,
      // Rumbo simulado para que el radar del demo también dibuje flechas.
      headingDeg: isMoving ? (seed * 37) % 360 : null,
      driverPhoto: null,
      fuelPercent: status === "alert" && seed % 3 === 0 ? 8 + (seed % 11) : 24 + (seed % 72),
      updatedAt: minutesAgo(status === "offline" ? 35 + (seed % 90) : 1 + (seed % 12)),
      position: {
        lat: 19.0414 + ((seed % 120) - 60) / 220,
        lng: -98.2063 + ((seed % 140) - 70) / 200,
      },
      aiMessage: t(`monitoreoIa.units.aiMessage.${status}`, {
        unit: unit.name,
        minutes: 5 + (seed % 40),
      }),
      chips: STATUS_CHIP_KEYS[status].map((chip) => ({
        label: t(`monitoreoIa.units.chips.${chip.key}`),
        tone: chip.tone,
      })),
      audioCount: status === "offline" ? 0 : 1 + (seed % 4),
      protocolCount: needsIncident ? 1 + (seed % 3) : seed % 2,
    };
  });
}

const PROTOCOL_SEEDS: readonly {
  id: string;
  nameKey: string;
  triggerKey: string;
  steps: readonly { actionId: ProtocolActionId; delayKey: string }[];
}[] = [
  {
    id: "proto-stop",
    nameKey: "unauthorizedStop",
    triggerKey: "unauthorizedStop",
    steps: [
      { actionId: "call_operator", delayKey: "immediate" },
      { actionId: "validate_keyword", delayKey: "immediate" },
      { actionId: "notify_supervisor", delayKey: "after2min" },
      { actionId: "escalate_incident", delayKey: "after5min" },
    ],
  },
  {
    id: "proto-jamming",
    nameKey: "jamming",
    triggerKey: "jamming",
    steps: [
      { actionId: "call_operator", delayKey: "immediate" },
      { actionId: "validate_keyword", delayKey: "immediate" },
      { actionId: "escalate_incident", delayKey: "immediate" },
      { actionId: "notify_client", delayKey: "after2min" },
    ],
  },
  {
    id: "proto-deviation",
    nameKey: "routeDeviation",
    triggerKey: "routeDeviation",
    steps: [
      { actionId: "call_operator", delayKey: "immediate" },
      { actionId: "log_incident", delayKey: "after2min" },
      { actionId: "notify_supervisor", delayKey: "after5min" },
    ],
  },
  {
    id: "proto-delivery",
    nameKey: "delivery",
    triggerKey: "delivery",
    steps: [
      { actionId: "call_operator", delayKey: "immediate" },
      { actionId: "request_photo", delayKey: "immediate" },
      { actionId: "notify_client", delayKey: "after2min" },
    ],
  },
];

export function buildProtocols(t: TFunction): Protocol[] {
  return PROTOCOL_SEEDS.map((seed) => ({
    id: seed.id,
    name: t(`monitoreoIa.protocolSeeds.${seed.nameKey}.name`),
    trigger: t(`monitoreoIa.protocolSeeds.${seed.nameKey}.trigger`),
    enabled: true,
    steps: seed.steps.map((step, index) => ({
      id: `${seed.id}-step-${index}`,
      actionId: step.actionId,
      delay: t(`monitoreoIa.delays.${step.delayKey}`),
    })),
  }));
}

const ROUTE_FINDING_KEYS = ["nightSegment", "riskZoneCrossing", "tightWindow", "noRestStop"] as const;

export function buildRouteInsights(t: TFunction, routeIds: string[]): RouteInsight[] {
  return routeIds.map((routeId) => {
    const seed = hashOf(routeId);
    const findingCount = 1 + (seed % 3);
    const riskScore = 20 + (seed % 70);
    const stopCount = 1 + (seed % 2);

    return {
      routeId,
      riskScore,
      findings: Array.from({ length: findingCount }, (_, index) =>
        t(`monitoreoIa.routeFindings.${pick(ROUTE_FINDING_KEYS, seed + index)}`),
      ),
      safetyScore: 100 - riskScore,
      verificationIntervalMin: pick([30, 45, 60, 90], seed),
      unsafeZones: riskScore >= 70 ? 1 + (seed % 2) : 0,
      departureWindow: t("monitoreoIa.routes.departureWindowValue", {
        from: `0${4 + (seed % 5)}:00`.slice(-5),
        to: `${10 + (seed % 6)}:00`,
      }),
      usedInTrips: seed % 12,
      createdByAi: seed % 3 === 0,
      durationMin: 90 + (seed % 300),
      stops: [
        { name: pick(DEMO_PLACES, seed).split(",")[0], kind: "origin" as const },
        ...Array.from({ length: stopCount }, (_, index) => ({
          name: pick(DEMO_PLACES, seed + index + 3).split(",")[0],
          kind: "stop" as const,
        })),
        { name: pick(DEMO_PLACES, seed + 9).split(",")[0], kind: "destination" as const },
      ],
    };
  });
}

const CUSTODY_WEIGHTS: readonly TripCustodyState[] = [
  "active",
  "active",
  "scheduled",
  "none",
  "active",
  "scheduled",
];

export function buildTripWatchStates(tripIds: string[], t?: TFunction): TripWatchState[] {
  return tripIds.map((tripId) => {
    const seed = hashOf(tripId);
    const custody = pick(CUSTODY_WEIGHTS, seed);
    const hasIncident = seed % 4 === 0;
    const progressPercent = 12 + (seed % 84);
    const isFinished = progressPercent >= 95;

    return {
      tripId,
      custody,
      calls: seed % 9,
      incidents: hasIncident ? 1 : 0,
      progressPercent,
      unitName: `U-${`${1 + (seed % 13)}`.padStart(2, "0")}`,
      driverName: pick(DEMO_DRIVERS, seed),
      departureAt: minutesAgo(60 + (seed % 300)),
      estimatedArrivalAt: minutesAgo(-(60 + (seed % 400))),
      actualArrivalAt: isFinished ? minutesAgo(15 + (seed % 60)) : null,
      nextCheckAt: custody === "active" ? minutesAgo(-(8 + (seed % 40))) : null,
      note:
        hasIncident && t
          ? t(`monitoreoIa.incidents.${pick(INCIDENT_KEYS, seed)}`)
          : null,
      createdByAi: seed % 2 === 0,
    };
  });
}

const AI_ROUTE_SEPARATOR = /\s+(?:→|->|a|hacia|hasta|to)\s+/i;

const trimTo = (value: string, max: number) =>
  value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value;

const placeFrom = (value: string, fallbackSeed: number) => {
  const clean = value.trim().replace(/[.,;:]+$/, "");
  return clean.length > 0 && clean.length <= 30
    ? clean
    : pick(DEMO_PLACES, fallbackSeed).split(",")[0];
};

export function buildAiRouteDraft(
  t: TFunction,
  prompt: string,
): { route: Route; insight: RouteInsight } {
  const clean = prompt.trim();
  const seed = hashOf(clean || "ai-route");
  const [rawOrigin = "", rawDestination = ""] = clean.split(AI_ROUTE_SEPARATOR);
  const origin = placeFrom(rawOrigin.split(/\s+/).slice(-3).join(" "), seed);
  const destination = placeFrom(rawDestination.split(/\s+/).slice(0, 3).join(" "), seed + 4);
  const id = `local-route-ai-${Date.now()}`;
  const riskScore = 22 + (seed % 62);

  return {
    route: {
      id,
      name: t("monitoreoIa.routes.aiRouteName", { origin, destination }),
      description: trimTo(clean, 120),
      status_id: "active",
      status: { id: "active", name: t("monitoreoIa.demo.statuses.active") },
      geofences_id: [],
      principal_route: [],
      principal_route_distance: 80_000 + (seed % 420) * 1_000,
      first_point_radius: 250,
      last_point_radius: 250,
      company_id: "local",
      vehicles_count: 1 + (seed % 4),
      createdAt: stamp(),
      updatedAt: stamp(),
    } as Route,
    insight: {
      routeId: id,
      riskScore,
      findings: Array.from({ length: 1 + (seed % 3) }, (_, index) =>
        t(`monitoreoIa.routeFindings.${pick(ROUTE_FINDING_KEYS, seed + index)}`),
      ),
      safetyScore: 100 - riskScore,
      verificationIntervalMin: pick([30, 45, 60, 90], seed),
      unsafeZones: riskScore >= 70 ? 1 : 0,
      departureWindow: t("monitoreoIa.routes.departureWindowValue", {
        from: `0${4 + (seed % 5)}:00`.slice(-5),
        to: `${10 + (seed % 6)}:00`,
      }),
      usedInTrips: 0,
      createdByAi: true,
      durationMin: 90 + (seed % 280),
      stops: [
        { name: origin, kind: "origin" as const },
        ...Array.from({ length: 1 + (seed % 2) }, (_, index) => ({
          name: pick(DEMO_PLACES, seed + index + 3).split(",")[0],
          kind: "stop" as const,
        })),
        { name: destination, kind: "destination" as const },
      ],
    },
  };
}

export function buildAiTripDraft(
  t: TFunction,
  prompt: string,
  route: { id: string; label: string } | undefined,
  unit: { id: string; label: string } | undefined,
): { trip: Trip; watch: TripWatchState } {
  const clean = prompt.trim();
  const seed = hashOf(clean || "ai-trip");
  const id = `local-trip-ai-${Date.now()}`;
  const [unitName, driverName] = (unit?.label ?? "").split(" · ");

  return {
    trip: {
      id,
      title: trimTo(clean, 60),
      description: clean,
      priority: pick(DEMO_TRIP_PRIORITIES, seed),
      route_id: route?.id,
      status_id: "scheduled",
      status: {
        id: "scheduled",
        name: t("monitoreoIa.demo.trips.statuses.scheduled"),
        color: "#3b82f6",
      },
      route: route ? { id: route.id, name: route.label } : null,
      createdAt: stamp(),
      updatedAt: stamp(),
    } as Trip,
    watch: {
      tripId: id,
      custody: "active",
      calls: 0,
      incidents: 0,
      progressPercent: 0,
      unitName: unitName ?? "",
      driverName: driverName ?? pick(DEMO_DRIVERS, seed),
      departureAt: minutesAgo(-(20 + (seed % 90))),
      estimatedArrivalAt: minutesAgo(-(180 + (seed % 300))),
      actualArrivalAt: null,
      nextCheckAt: minutesAgo(-(15 + (seed % 30))),
      note: null,
      createdByAi: true,
    },
  };
}

const DEMO_THREADS = [
  { id: "ct-1", group: "today", icon: "ri-shield-check-line" },
  { id: "ct-2", group: "today", icon: "ri-truck-line" },
  { id: "ct-3", group: "yesterday", icon: "ri-map-pin-5-line" },
  { id: "ct-4", group: "yesterday", icon: "ri-alarm-warning-line" },
  { id: "ct-5", group: "earlier", icon: "ri-road-map-line" },
  { id: "ct-6", group: "earlier", icon: "ri-gas-station-line" },
] as const satisfies readonly Pick<ChatThreadSummary, "id" | "group" | "icon">[];

export function buildDemoChatThreads(t: TFunction): ChatThreadSummary[] {
  return DEMO_THREADS.map((thread) => ({
    ...thread,
    title: t(`monitoreoIa.chatPage.threads.${thread.id}.title`),
    preview: t(`monitoreoIa.chatPage.threads.${thread.id}.preview`),
  }));
}
